import datapoints from './datapoints';
import showNotificationOnSchedule from './helpers/showNotificationOnSchedule';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { getApolloClient } from 'owa-apollo';
import { getISOString, OwaDate } from 'owa-datetime';
import deleteConversationStoreUpdate from 'owa-mail-actions/lib/triage/deleteConversationStoreUpdate';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import { ApplyConversationActionContext } from 'owa-mail-triage-action-utils';
import * as undoActions from 'owa-mail-undo';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import {
    getRowIdsFromRowKeys,
    filterConversationRowsByRestriction,
    getApplyConversationActionContext,
    TableView,
} from 'owa-mail-list-store';
import {
    ScheduleConversationDocument,
    ScheduleConversationMutation,
} from '../graphql/__generated__/scheduleConversationMutation.interface';

import {
    UndoScheduleConversationDocument,
    UndoScheduleConversationMutation,
} from '../graphql/__generated__/undoScheduleConversationMutation.interface';

type ScheduleConversationResult = ScheduleConversationMutation['scheduleConversation'];
type UndoScheduleConversationResult = UndoScheduleConversationMutation['undoScheduleConversation'];

/**
 * Schedule or Unschedule conversations
 * @param conversationRowKeys the row keys
 * @param tableView the tableView
 * @param actionSouce the actionSource
 * @param scheduleReturnTime the time conversation will return or undefined to unschedule
 */
export default createLegacyActionWithDatapoint(
    'ScheduleConversation',
    datapoints.scheduleItems
)(function scheduleConversations(
    conversationRowKeys: string[],
    tableView: TableView,
    actionSource: ActionSource,
    scheduleReturnTime: OwaDate | undefined
): Promise<void> {
    const tableViewId = tableView.id;
    const filteredRowKeys = filterConversationRowsByRestriction(
        conversationRowKeys,
        tableViewId,
        tableConversationRelation => !!tableConversationRelation
    );

    if (filteredRowKeys.length == 0) {
        return Promise.resolve();
    }

    const conversationIdStrings = getRowIdsFromRowKeys(filteredRowKeys, tableViewId);
    const conversationActionContexts = filteredRowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableViewId)
    );

    const folderId = tableView.tableQuery.folderId;
    const scheduledFolderId = folderNameToId('scheduled');
    const isScheduledFolder = folderId === scheduledFolderId;

    undoActions.clearLastUndoableAction();
    showNotificationOnSchedule(!isScheduledFolder, scheduleReturnTime);

    // 1. Make local update when not in scheduled folder
    if (!isScheduledFolder) {
        deleteConversationStoreUpdate(conversationIdStrings, tableView, 'Snooze');
    }

    // 2. Make service request
    // When scheduling conversation we should act upon folderId and not on serverFolderId
    // so that entire conversation and not just the items belonging to the serverFolder get scheduled
    const scheduleConversationsPromise = invokeScheduleConversationMutation(
        conversationActionContexts,
        folderId /* contextFolderId */,
        scheduleReturnTime
    );

    // Do not allow undo within scheduled as we have no way to recover ReturnTime
    if (!isScheduledFolder) {
        undoActions.addActionToUndoStack(
            async () => {
                const response = await scheduleConversationsPromise;
                if (response.success) {
                    // Perform unschedule
                    await invokeUndoScheduleConversationMutation(
                        conversationActionContexts,
                        scheduledFolderId /* contextFolderId */,
                        response.undoRequestId
                    );
                }
                return Promise.resolve();
            },
            tableView.serverFolderId,
            scheduleConversationsPromise /* pass actionPromise for DismissUndo */
        );
    }

    return processApplyConversationActionResponseMessage(
        scheduleConversationsPromise,
        filteredRowKeys,
        tableView.serverFolderId,
        tableView,
        false /* shouldRemoveRowsFromTombstone */,
        true /* skipCheckingResponseMessage */
    );
});

/**
 * Invoke the ScheduleConversation graphql mutation
 * @param conversationContexts conversation contexts
 * @param contextFolderId the contextFolderId
 * @param scheduleReturnTime the time item will return or undefined to unschedule
 * @return the ScheduleConversationResult result
 */
async function invokeScheduleConversationMutation(
    conversationContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    scheduleReturnTime: OwaDate | undefined
): Promise<ScheduleConversationResult> {
    const conversationSnapShots = conversationContexts.map(context => {
        return {
            id: context.conversationId,
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: contextFolderId,
            scheduleReturnTime: scheduleReturnTime ? getISOString(scheduleReturnTime) : null,
        },
        mutation: ScheduleConversationDocument,
    });
    return result?.data?.scheduleConversation;
}

/**
 * Invoke the UndoScheduleConversation graphql mutation
 * @param conversationContexts conversation contexts
 * @param contextFolderId the contextFolderId
 * @param undoRequestId the undo requestId returned by Hx to undo this request
 * @return the ScheduleConversationResult result
 */
async function invokeUndoScheduleConversationMutation(
    conversationContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    undoRequestId: number | null | undefined
): Promise<UndoScheduleConversationResult> {
    const conversationSnapShots = conversationContexts.map(context => {
        return {
            id: context.conversationId,
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: contextFolderId,
            undoRequestId: undoRequestId,
        },
        mutation: UndoScheduleConversationDocument,
    });
    return result?.data?.undoScheduleConversation;
}
